import { ApolloClient } from '@apollo/client';
import * as sentry from '@sentry/nextjs';
import { getCountryByCurrency } from '@utils/locations';

import { GiftCardCheckoutPayload } from '../types/GiftCard';
import { CurrencyCode } from '../types/Locations';
import { Frame } from '../types/Products';
import type {
  AttributeInput,
  CheckoutGateway,
  CollectionSetProduct,
  ShopifyProductPurchaseDetails,
  ShopifyProductToCheckout,
} from '../types/Purchasing';
import {
  CreateCartWithProductsDocument,
  ShopifyGenCreateCartWithProductsMutation,
  ShopifyGenUpdateCartWithProductsMutation,
  UpdateCartWithProductsDocument,
} from '../types/shopifygen';
import {
  TrackBasketCheckout,
  TrackCheckout,
  TrackGiftCardCheckout,
} from '../utils/analytics';

export const SHOPIFY_LINE_ITEM_UNLOCK_CODE_PROPERTY = '_UnlockCode';

export class ShopifyCheckoutGateway implements CheckoutGateway<
  ShopifyProductPurchaseDetails,
  GiftCardCheckoutPayload
> {
  constructor(private shopifyClient: ApolloClient<any>) {}

  public async checkoutWithProduct({
    collectionSetProducts,
    currency,
    customAttributes,
    frame,
    product,
    productAttributes,
    quantity,
    unlockCode,
  }: {
    collectionSetProducts: CollectionSetProduct[];
    currency: CurrencyCode;
    customAttributes?: AttributeInput[];
    frameQuantity?: number;
    frame: Frame | null;
    product: ShopifyProductPurchaseDetails;
    productAttributes?: AttributeInput[];
    quantity: number;
    unlockCode?: string;
  }): Promise<string> {
    const { data, errors } =
      await this.shopifyClient.mutate<ShopifyGenCreateCartWithProductsMutation>(
        {
          mutation: CreateCartWithProductsDocument,
          variables: {
            currency,
            country: getCountryByCurrency(currency),
            lineItems: lineItemsForProductRequest({
              product,
              productAttributes,
              quantity,
              unlockCode,
              frame,
              collectionSetProducts,
            }),
            customAttributes: customAttributes || [],
          },
        },
      );

    if (
      errors?.length ||
      !data?.cartCreate?.cart?.checkoutUrl ||
      !data?.cartCreate?.cart?.id
    ) {
      sentry.captureException({ message: 'Failed to create cart', errors });
      throw new Error('Failed to create cart');
    }

    const lineItemsContainDiscount = data.cartCreate.cart.lines.nodes.some(
      ShopifyCheckoutGateway.containsDiscountPredicate,
    );

    if (lineItemsContainDiscount) {
      await this.applyDiscountAttributesToLineItems(data);
    }

    const productCategory = product.tags.reduce<string[]>((acc, tag) => {
      if (tag.toLowerCase() === 'print edition') {
        acc.push('print');
      }

      if (tag.toLowerCase() === 'sculpture edition') {
        acc.push('sculpture');
      }

      if (tag.toLowerCase() === 'hand finished') {
        acc.push('hand');
      }

      if (tag.toLowerCase() === 'collectibles') {
        acc.push('collectibles');
      }

      if (tag.toLowerCase() === 'nfts') {
        acc.push('ntfs');
      }

      if (tag.toLowerCase() === 'original works') {
        acc.push('original');
      }

      if (tag.toLowerCase() === 'studio works') {
        acc.push('studio');
      }

      return acc;
    }, []);

    await TrackCheckout({
      productTitle: product.name,
      productCurrencyCode: product.currency,
      productPrice: product.price,
      numberOfEditions: product.numberOfEditions,
      productId: product.shopifyVariantId,
      productIdMain: product.shopifyId,
      frameId: frame?.variant.consolidatedVariants
        .map(({ shopifyFrameId }) => shopifyFrameId)
        .join(','),
      framePrice: frame?.variant.consolidatedVariants.reduce(
        (acc, v) => acc + v.price,
        0,
      ),
      isFramed: !!frame,
      canBeFramed: product.separateFrameAvailable,
      quantity,
      checkoutId: data.cartCreate.cart.id,
      frameUpgradeAvailable: Boolean(product.frames),
      frameVariantTitle: frame?.title,
      artist: product?.artist ?? '',
      productCategory,
      extras: {},
    });

    return data.cartCreate.cart.checkoutUrl;
  }

  public async checkoutWithListOfProducts(
    productsToCheckout: ShopifyProductToCheckout[],
    currency: CurrencyCode,
    customAttributes?: AttributeInput[],
  ): Promise<string> {
    const { data, errors } =
      await this.shopifyClient.mutate<ShopifyGenCreateCartWithProductsMutation>(
        {
          mutation: CreateCartWithProductsDocument,
          variables: {
            currency,
            country: getCountryByCurrency(currency),
            lineItems: productsToCheckout.map(shopifyProductToLineItem),
            customAttributes: customAttributes || [],
          },
        },
      );

    if (
      errors?.length ||
      !data?.cartCreate?.cart?.checkoutUrl ||
      !data?.cartCreate?.cart?.id
    ) {
      sentry.captureException({ message: 'Failed to create cart', errors });
      throw new Error('Failed to create cart');
    }

    const lineItemsContainDiscount = data.cartCreate.cart.lines.nodes.some(
      ShopifyCheckoutGateway.containsDiscountPredicate,
    );

    if (lineItemsContainDiscount) {
      await this.applyDiscountAttributesToLineItems(data);
    }

    await TrackBasketCheckout({
      basket: productsToCheckout.map((basketEntry) => ({
        productId: basketEntry.product.productId,
        productPrice: basketEntry.product.price,
        productTitle: basketEntry.product.productTitle,
        quantity: basketEntry.quantity,
        customAttributes: basketEntry.customProperties,
      })),
      checkoutId: data.cartCreate.cart.id,
      customAttributes,
    });

    return data.cartCreate.cart.checkoutUrl;
  }

  public async checkoutWithGiftCard(
    giftCardPayload: GiftCardCheckoutPayload,
    currency: CurrencyCode,
  ): Promise<string> {
    const { data, errors } =
      await this.shopifyClient.mutate<ShopifyGenCreateCartWithProductsMutation>(
        {
          mutation: CreateCartWithProductsDocument,
          variables: {
            currency,
            country: getCountryByCurrency(currency),
            lineItems: lineItemsForGiftCardRequest(
              giftCardPayload.recipientName,
              giftCardPayload.senderName,
              giftCardPayload.message,
              giftCardPayload.giftCardVariantId
                ? giftCardPayload.giftCardVariantId
                : '',
              giftCardPayload.imageCredentialsTitle,
              giftCardPayload.imageCredentialsDescription,
            ),
            customAttributes: [],
          },
        },
      );

    if (
      errors?.length ||
      !data?.cartCreate?.cart?.checkoutUrl ||
      !data?.cartCreate?.cart?.id
    ) {
      sentry.captureException({ message: 'Failed to create cart', errors });
      throw new Error('Failed to create cart');
    }

    const lineItemsContainDiscount = data.cartCreate.cart.lines.nodes.some(
      ShopifyCheckoutGateway.containsDiscountPredicate,
    );

    if (lineItemsContainDiscount) {
      await this.applyDiscountAttributesToLineItems(data);
    }

    giftCardPayload.giftCardVariantId &&
      giftCardPayload.giftCardValue &&
      (await TrackGiftCardCheckout({
        giftCardVariantId: giftCardPayload.giftCardVariantId,
        giftCardValue: giftCardPayload.giftCardValue,
        customization:
          giftCardPayload.message !== null &&
          giftCardPayload.message.length > 0,
      }));

    return data.cartCreate.cart.checkoutUrl;
  }

  private async applyDiscountAttributesToLineItems(
    data: ShopifyGenCreateCartWithProductsMutation,
  ): Promise<void> {
    if (!data?.cartCreate?.cart?.checkoutUrl || !data?.cartCreate?.cart?.id) {
      sentry.captureException({ message: 'Failed to create cart' });
      throw new Error('Failed to create cart');
    }

    try {
      await this.shopifyClient.mutate<ShopifyGenUpdateCartWithProductsMutation>(
        {
          mutation: UpdateCartWithProductsDocument,
          variables: {
            cartId: data.cartCreate.cart.id,
            lines: data.cartCreate.cart.lines.nodes.map((line) => {
              const attributes = line.attributes.map(({ key, value }) => ({
                key,
                value,
              }));
              const totalDiscount = line.discountAllocations.reduce(
                (sum, item) => sum + Number(item.discountedAmount.amount),
                0,
              );
              const totalPrice =
                Number(line.cost.totalAmount.amount) + totalDiscount;
              // prices are converted to fractions before division to avoid floating point errors
              const priceFractionAfterDiscount =
                1 - (totalDiscount * 100) / (totalPrice * 100);

              return {
                id: line.id,
                attributes: [
                  ...attributes,
                  {
                    key: '_priceFractionAfterDiscount',
                    value: priceFractionAfterDiscount.toString(),
                  },
                ],
              };
            }),
          },
        },
      );
    } catch (error) {
      sentry.captureException({ message: 'Failed to update cart', error });
    }
  }

  static containsDiscountPredicate = (line: {
    discountAllocations: Array<unknown>;
  }) => Boolean(line.discountAllocations.length);
}

const shopifyProductToLineItem = (
  shopifyProductToCheckout: ShopifyProductToCheckout,
): {
  merchandiseId: string;
  quantity: number;
  attributes?: AttributeInput[];
} => {
  const attributes = [];

  if (shopifyProductToCheckout.unlockCode) {
    attributes.push({
      key: SHOPIFY_LINE_ITEM_UNLOCK_CODE_PROPERTY,
      value: shopifyProductToCheckout.unlockCode,
    });
  }

  shopifyProductToCheckout.customProperties &&
    attributes.push(...shopifyProductToCheckout.customProperties);

  return {
    merchandiseId: shopifyProductToCheckout.product.shopifyVariantId,
    quantity: shopifyProductToCheckout.quantity,
    attributes: attributes.length > 0 ? attributes : undefined,
  };
};

const lineItemsForProductRequest = ({
  collectionSetProducts,
  frame,
  product,
  productAttributes,
  quantity,
  unlockCode,
}: {
  collectionSetProducts: CollectionSetProduct[];
  frame: Frame | null;
  product: ShopifyProductPurchaseDetails;
  productAttributes?: AttributeInput[];
  quantity: number;
  unlockCode?: string;
}) => {
  const productLineItems: Record<string, any>[] = collectionSetProducts.length
    ? collectionSetProducts.map((release) => ({
        merchandiseId: release.shopifyVariantId,
        quantity: release.quantity,
      }))
    : [
        {
          merchandiseId: product.shopifyVariantId,
          quantity,
        },
      ];

  const attributes: AttributeInput[] = [
    ...(unlockCode
      ? [{ key: SHOPIFY_LINE_ITEM_UNLOCK_CODE_PROPERTY, value: unlockCode }]
      : []),
    ...(productAttributes ?? []),
  ];

  if (attributes.length) {
    productLineItems[0].attributes = attributes;
  }

  if (!frame) {
    return productLineItems;
  }

  const frameLineItems = frame.variant.consolidatedVariants.map(
    (consolidatedVariant) => ({
      merchandiseId: consolidatedVariant.shopifyFrameVariantId,
      quantity: consolidatedVariant.quantity,
    }),
  );

  return [...frameLineItems, ...productLineItems];
};

const lineItemsForGiftCardRequest = (
  recipientName: string | null,
  senderName: string | null,
  message: string | null,
  giftCardVariantId: string,
  imageCredentialsTitle: string,
  imageCredentialsDescription: string,
) => {
  const giftCardLineItem: {
    quantity: number;
    merchandiseId: string;
    attributes: Array<{ key: string; value: string }>;
  } = {
    quantity: 1,
    merchandiseId: giftCardVariantId,
    attributes: [],
  };

  giftCardLineItem.attributes = [
    { key: '_imageCredentialsTitle', value: imageCredentialsTitle },
    {
      key: '_imageCredentialsDescription',
      value: imageCredentialsDescription,
    },
  ];
  if (recipientName && senderName && message) {
    giftCardLineItem.attributes = [
      ...giftCardLineItem.attributes,
      { key: '_recipientName', value: recipientName },
      { key: '_senderName', value: senderName },
      { key: '_message', value: message },
    ];
  }

  return [giftCardLineItem];
};
